/**
 * Camada 1 · o painel que o sino abre: a lista de conversas, e a conversa aberta.
 *
 * POR QUE ESTA PECA EXISTE, se `PainelConversa` ja desenha uma conversa: aquele componente
 * mostra UMA, e precisa saber qual. Quem escolhe e a pessoa, e a escolha acontece numa
 * lista. Sem esta peca, cada produto escreveria a propria lista, e as duas divergiriam.
 *
 * A NAVEGACAO E DE UM NIVEL SO, de proposito: lista, ou conversa. Painel lateral de 20rem
 * nao comporta hierarquia, e no celular ele e a tela inteira, onde voltar precisa ser um
 * gesto obvio e nao uma trilha.
 */

import { useCallback, useEffect, useState } from 'react';
import { EstadoErro, EstadoVazio } from '@complexity/ui';
import type { ComunicadoRecebido, Conversa, TransporteChat } from '../index.js';
import { PainelConversa } from './PainelConversa.js';

export interface PainelConversasProps {
  /**
   * O produto de quem escreve, para a conversa nascer amarrada a ele.
   *
   * OBRIGATORIO: sem produto, a operacao receberia uma conversa sem saber de qual sistema
   * ela veio, e a tela de Mensagens do Super Admin filtra justamente por isso.
   */
  readonly tenantId: string;
  readonly transporte: TransporteChat;
  /** Quem esta olhando. Decide o lado da bolha e o que conta como nao lida. */
  readonly eu: 'cliente' | 'operacao';
  /*
   * NAO HA `aoFechar`, e a ausencia e decisao dele em 07/09/2026: "nao havera OPCAO
   * PARA FECHAR AS MENSAGENS - RETIRAR ESSE X".
   *
   * POR QUE ELA NAO FAZ FALTA: Mensagens e uma TELA, com o menu lateral do lado, e nao
   * um painel sobreposto. Quem quer sair clica em outro item do menu, que e o mesmo
   * gesto de qualquer outra tela. O X levava para a raiz, e era um segundo caminho de
   * saida que so existia aqui.
   */
}

export function PainelConversas({
  tenantId,
  transporte,
  eu,
}: PainelConversasProps) {
  const [conversas, definirConversas] = useState<readonly Conversa[]>([]);
  const [carregando, definirCarregando] = useState(true);
  const [falha, definirFalha] = useState<string | null>(null);
  const [aberta, definirAberta] = useState<Conversa | null>(null);

  /**
   * O FORMULARIO DE ABRIR SAIU DAQUI em 07/09/2026, por decisao dele: "NAO VAI TER MENSAGEM
   * INICIADA PELO USUARIO SEM SER PELA AJUDA (MENU)".
   *
   * Ele existiu, e o motivo de existir era real: sem ele a pessoa via "Nenhuma mensagem
   * ainda" e nao tinha como comecar. O que mudou nao foi a necessidade, foi a PORTA: a
   * conversa passa a nascer na Ajuda, onde ela ja carrega o artigo que a pessoa leu e nao
   * resolveu. Quem responde deixa de ter que perguntar de volta o que aconteceu.
   *
   * O estado de envio saiu junto: ele so servia a este formulario. A resposta dentro de
   * uma conversa aberta vive no PainelConversa, que tem o proprio.
   */

  /**
   * OS COMUNICADOS (ADR 0055), e eles vivem em estado PROPRIO.
   *
   * Nao entram na lista de `conversas` de proposito: comunicado e anuncio, tem audiencia
   * declarada e nao espera resposta; conversa e 1:1 e espera. Misturados no mesmo array, a
   * pergunta *"quem esta esperando resposta?"* passaria a contar anuncio, que e exatamente o
   * defeito que aquele ADR existe para impedir.
   *
   * O SINO JA OS CONTAVA ANTES DESTA LISTA EXISTIR, e por algumas horas foi assim: ele somava
   * comunicado e o painel nao mostrava nenhum. Um sino que marca 3 e abre sem nada faz a
   * pessoa concluir que o sistema quebrou, e e pior que sino que nao conta.
   */
  const [comunicados, definirComunicados] = useState<readonly ComunicadoRecebido[]>([]);
  const [comunicadoAberto, definirComunicadoAberto] = useState<ComunicadoRecebido | null>(null);

  const carregar = useCallback(() => {
    definirCarregando(true);
    definirFalha(null);

    /*
      OS COMUNICADOS VAO JUNTO, e a chamada e disparada AQUI e nao num efeito proprio: dois
      efeitos independentes produziriam duas ondas de renderizacao e, pior, a lista voltaria
      em dois momentos diferentes, com o painel pulando na frente de quem esta lendo.

      ELE NAO ENTRA NO `then` DAS CONVERSAS, e essa e a assimetria deliberada: se os
      comunicados falharem, as conversas continuam aparecendo. O caminho contrario nao vale, e
      a razao esta no `catch` abaixo: sem a lista de conversas o painel nao tem o que mostrar.

      `listarComunicados` E OPCIONAL no contrato, entao a chamada e condicional: o servico no
      ar pode ser anterior a rota, e as fachadas dos testes implementam so o que precisam.
    */
    void transporte
      .listarComunicados?.(tenantId)
      .then((lista) => definirComunicados(lista))
      .catch(() => definirComunicados([]));

    return transporte
      .listarConversas(tenantId)
      .then((lista) => {
        definirConversas(lista);
        definirCarregando(false);
      })
      .catch(() => {
        // FALHA E ESTADO PROPRIO, e nao lista vazia: os dois se parecem na tela e sao
        // opostos. Vazio diz "nao ha conversa"; falha diz "nao consegui olhar", e confundi-los
        // faz a pessoa concluir que ninguem escreveu.
        definirFalha('A lista de conversas não respondeu.');
        definirCarregando(false);
      });
  }, [transporte, tenantId]);

  useEffect(() => {
    let vivo = true;
    void carregar().then(() => {
      if (!vivo) return;
    });
    return () => {
      vivo = false;
    };
  }, [carregar]);

  // Escape VOLTA UM NIVEL, e nada alem disso: da conversa para a lista.
  //
  // NA LISTA ELE NAO FAZ NADA, e isso mudou em 07/09/2026 por decisao dele: "nao havera
  // OPCAO PARA FECHAR AS MENSAGENS". Ele fechava o painel dali, e fechar por tecla e uma
  // opcao de fechar como qualquer outra, so que invisivel.
  //
  // O RAMO DE DENTRO FICA porque ele e NAVEGACAO: quem abriu uma conversa e apertou Escape
  // quer voltar para a lista, e nao sair das Mensagens.
  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return;
      if (aberta === null) return;
      definirAberta(null);
      // A lista e relida ao voltar: as mensagens que a conversa acabou de marcar como
      // lidas mudaram a contagem, e uma lista velha mostraria o numero de antes.
      void carregar();
    }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberta, carregar]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-cx-3 border-b border-borda p-cx-4">
        <h2 className="text-cx-lg font-semibold text-texto">
          {aberta ? aberta.assunto : 'Mensagens'}
        </h2>
        {/*
          NAO HA BOTAO DE ESCREVER AQUI, e a ausencia e decisao dele em 07/09/2026:
          "NAO VAI TER MENSAGEM INICIADA PELO USUARIO SEM SER PELA AJUDA (MENU)".

          POR QUE ISSO IMPORTA: conversa aberta daqui chega sem contexto nenhum, e quem
          responde precisa perguntar de volta o que a pessoa estava tentando fazer. Nascida
          na Ajuda, ela ja vem com o artigo que a pessoa leu e nao resolveu, e a primeira
          resposta pode ser a resposta.
        */}
      </div>

      {comunicadoAberto !== null ? (
        /*
          A LEITURA DO COMUNICADO (ADR 0055).

          ELA E UM RAMO PROPRIO, e nao o `PainelConversa` com o texto dentro: aquele painel
          desenha uma CONVERSA, com campo de resposta e marcacao de lida por mensagem. O
          comunicado nao tem nenhum dos dois, e reaproveitar o painel ofereceria um campo de
          resposta que nao leva a lugar nenhum.

          RESPONDER A UM COMUNICADO CRIA UMA CONVERSA, e essa e a sexta consequencia daquele
          ADR. Ela ainda NAO existe: `veio_de_comunicado` esta na tabela e nao ha caminho na
          tela. Por isso aqui nao ha botao de responder: um botao que nao faz nada e pior que
          a ausencia dele, e a ausencia esta declarada no backlog.
        */
        <div className="flex min-h-0 flex-1 flex-col">
          <button
            type="button"
            onClick={() => definirComunicadoAberto(null)}
            className="min-h-[44px] px-cx-4 text-left text-cx-sm text-marca"
          >
            Voltar às mensagens
          </button>
          <div className="min-h-0 flex-1 overflow-y-auto p-cx-4">
            <h2 className="text-cx-base font-semibold text-texto">{comunicadoAberto.assunto}</h2>
            <p className="mt-cx-1 text-cx-xs text-texto-suave">
              {comunicadoAberto.publicadoEm.toLocaleString('pt-BR')}
            </p>
            {/* `whitespace-pre-wrap` PORQUE O TEXTO FOI ESCRITO EM VARIAS LINHAS: quem envia
                digita paragrafos num campo de texto longo, e sem isto tudo chega grudado numa
                linha so, dizendo outra coisa. */}
            <p className="mt-cx-3 whitespace-pre-wrap text-cx-sm leading-relaxed text-texto">
              {comunicadoAberto.texto}
            </p>
            {/* A AUSENCIA DECLARADA: em quatro dos cinco alcances nao ha onde gravar que esta
                pessoa leu, e a tela DIZ isso em vez de fingir que anotou. */}
            {!comunicadoAberto.temRegistroDeLeitura && (
              <p className="mt-cx-4 text-cx-xs text-texto-suave">
                Este comunicado foi enviado a um grupo. A leitura não é registrada por pessoa.
              </p>
            )}
          </div>
        </div>
      ) : aberta === null ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-cx-4">
          {carregando && <p className="text-cx-sm text-texto-suave">Carregando…</p>}

          {!carregando && falha !== null && (
            <EstadoErro titulo="As mensagens não abriram" mensagem={falha}>
              Nada foi perdido. Feche e abra de novo em instantes.
            </EstadoErro>
          )}

          {!carregando && falha === null && conversas.length === 0 && (
            <EstadoVazio
              titulo="Nenhuma mensagem ainda"
            >
              Nenhuma conversa ainda. Para falar com a nossa equipe, abra a Ajuda no menu: a conversa nasce lá, junto do que você estava tentando resolver.
            </EstadoVazio>
          )}

          {/*
            OS COMUNICADOS VEM ACIMA DAS CONVERSAS, e a ordem nao e hierarquia: e que o
            comunicado NAO ESPERA RESPOSTA. Quem abre o painel com uma conversa pendente
            precisa ve-la, e quem abre por causa do sino precisa achar o anuncio que fez o
            numero subir. Embaixo, o anuncio ficaria atras de uma lista que pode ser longa.

            ELES APARECEM MESMO COM AS CONVERSAS CARREGANDO, porque sao duas fontes
            independentes: prender o anuncio a lista de conversas faria a falha de uma
            esconder a outra.
          */}
          {comunicados.length > 0 && (
            <ul className="mb-cx-2 flex flex-col gap-cx-2">
              {comunicados.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      definirComunicadoAberto(c);
                      // MARCA AO ABRIR, e nao ao fechar: fechar pode nao acontecer (a pessoa
                      // sai da tela, o navegador cai), e o comunicado voltaria como nao lido
                      // para sempre. `void` porque a tela nao espera: se falhar, o pior caso
                      // e ele aparecer nao lido de novo, e nao a leitura travar.
                      void transporte.marcarComunicadoLido?.(tenantId, c.id).then((r) => {
                        if (!r.registrado) return;
                        definirComunicados((atual) =>
                          atual.map((x) => (x.id === c.id ? { ...x, lidoEm: new Date() } : x)),
                        );
                      });
                    }}
                    className="min-h-[44px] w-full rounded-cx-md border border-marca-suave bg-marca-suave p-cx-3 text-left hover:border-marca"
                  >
                    <span className="flex items-center justify-between gap-cx-2">
                      <span className="text-cx-sm font-medium text-texto">{c.assunto}</span>
                      {/* A ETIQUETA E O QUE SEPARA os dois na mesma lista. Sem ela, o anuncio
                          pareceria uma conversa que ninguem respondeu. */}
                      <span className="rounded-full bg-marca px-cx-2 text-[11px] font-semibold leading-[18px] text-superficie">
                        {c.lidoEm === null ? 'Novo' : 'Comunicado'}
                      </span>
                    </span>
                    <span className="mt-cx-1 block text-cx-xs text-texto-suave">
                      {c.publicadoEm.toLocaleString('pt-BR')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!carregando && falha === null && conversas.length > 0 && (
            <ul className="flex flex-col gap-cx-2">
              {conversas.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => definirAberta(c)}
                    className="min-h-[44px] w-full rounded-cx-md border border-borda p-cx-3 text-left hover:border-marca"
                  >
                    <span className="flex items-center justify-between gap-cx-2">
                      <span className="text-cx-sm font-medium text-texto">{c.assunto}</span>
                      {/* O NUMERO SEM PREFIXO DE LARGURA: o defeito de 24/08 no legado era
                          um contador certo que nao existia no celular. */}
                      {c.naoLidas > 0 && (
                        <span className="flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-marca px-[4px] text-[11px] font-semibold leading-none text-superficie">
                          {c.naoLidas}
                        </span>
                      )}
                    </span>
                    <span className="mt-cx-1 block text-cx-xs text-texto-suave">
                      {c.atualizadaEm.toLocaleString('pt-BR')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <button
            type="button"
            onClick={() => {
              definirAberta(null);
              void carregar();
            }}
            className="min-h-[44px] px-cx-4 text-left text-cx-sm text-marca"
          >
            Voltar às conversas
          </button>
          <div className="min-h-0 flex-1">
            <PainelConversa
              tenantId={tenantId}
              conversaId={aberta.id}
              transporte={transporte}
              eu={eu}
            />
          </div>
        </div>
      )}
    </div>
  );
}
